/**
 * DonorEventForm — full-page create/edit form for a donor event.
 *
 * Replaces the `max-w-md` create modal. Donor events also had NO edit path at all
 * before this: once created, a wrong date, venue or meeting link could never be
 * corrected. The form doubles as both, driven by whether an event is passed in.
 *
 * It also exposes the guest list at creation time — the API accepted `cohort_id`
 * but nothing ever acted on it, so attaching a cohort did nothing. Choosing one
 * here fans it out to real invitations.
 */

import React, { useEffect, useState } from 'react';
import { ArrowLeft, CalendarPlus, Loader2, Save, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DonorEvent, DonorEventCreate, InviteChannel } from '@/services/api/donorEvents';
import { DonorCohort, listCohorts } from '@/services/api/donorCohorts';

interface Props {
  event?: DonorEvent | null;
  saving: boolean;
  error?: string | null;
  onCancel: () => void;
  onSubmit: (data: DonorEventCreate) => void;
}

/** <input type="datetime-local"> needs a local `YYYY-MM-DDTHH:mm`, not an ISO Z string. */
function toLocalInput(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const CHANNELS: { id: InviteChannel; label: string; hint: string }[] = [
  { id: 'email', label: 'Email', hint: 'Includes a calendar invite (.ics)' },
  { id: 'sms', label: 'SMS', hint: 'Needs a mobile number on the record' },
  { id: 'portal', label: 'Portal', hint: 'Only for donors with a portal login' },
];

export const DonorEventForm: React.FC<Props> = ({ event, saving, error, onCancel, onSubmit }) => {
  const isEdit = !!event;
  const [title, setTitle] = useState(event?.title || '');
  const [description, setDescription] = useState(event?.description || '');
  const [start, setStart] = useState(toLocalInput(event?.start_time));
  const [end, setEnd] = useState(toLocalInput(event?.end_time));
  const [locationType, setLocationType] = useState(event?.location_type || 'in_person');
  const [address, setAddress] = useState(event?.location_address || '');
  const [meetingLink, setMeetingLink] = useState(event?.meeting_link || '');
  const [cohortId, setCohortId] = useState<string>(event?.cohort_id ? String(event.cohort_id) : '');
  const [inviteNow, setInviteNow] = useState(false);
  const [channels, setChannels] = useState<InviteChannel[]>(['email']);
  const [cohorts, setCohorts] = useState<DonorCohort[]>([]);

  useEffect(() => {
    listCohorts()
      .then(setCohorts)
      .catch(() => setCohorts([]));
  }, []);

  const canSave = title.trim() && start && end && !saving;
  const selectedCohort = cohorts.find((c) => String(c.id) === cohortId);

  const submit = () => {
    if (!canSave) return;
    onSubmit({
      title: title.trim(),
      description: description.trim() || undefined,
      start_time: new Date(start).toISOString(),
      end_time: new Date(end).toISOString(),
      location_type: locationType,
      location_address: address.trim() || undefined,
      meeting_link: meetingLink.trim() || undefined,
      cohort_id: cohortId ? Number(cohortId) : undefined,
      invite_cohort: !isEdit && inviteNow && !!cohortId,
      invite_channels: channels,
    });
  };

  const toggleChannel = (c: InviteChannel) =>
    setChannels((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));

  const field = 'w-full rounded border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';
  const label = 'mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300';

  return (
    <div className="mx-auto max-w-3xl space-y-5 p-4 md:p-6 lg:p-8">
      <div className="flex items-center gap-3">
        <button
          onClick={onCancel}
          className="rounded p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
          aria-label="Back to donor events"
        >
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            {isEdit ? 'Edit donor event' : 'New donor event'}
          </h1>
          <p className="text-sm text-gray-500">
            {isEdit ? event?.title : 'Galas, cultivation dinners, house parties, webinars.'}
          </p>
        </div>
      </div>

      {error ? (
        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-900/20 dark:text-red-300">
          {error}
        </div>
      ) : null}

      <section className="space-y-4 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Details</h2>
        <div>
          <label className={label}>Event name</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} className={field} placeholder="Spring Gala 2026" />
        </div>
        <div>
          <label className={label}>Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className={field}
            placeholder="What the evening is for, who's speaking, dress code…"
          />
          <p className="mt-1 text-xs text-gray-400">Shown on the public RSVP page.</p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className={label}>Starts</label>
            <input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} className={field} />
          </div>
          <div>
            <label className={label}>Ends</label>
            <input type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} className={field} />
          </div>
        </div>
      </section>

      <section className="space-y-4 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Location</h2>
        <div>
          <label className={label}>Format</label>
          <select value={locationType} onChange={(e) => setLocationType(e.target.value)} className={field}>
            <option value="in_person">In person</option>
            <option value="virtual">Virtual</option>
            <option value="hybrid">Hybrid</option>
          </select>
        </div>
        {locationType !== 'virtual' ? (
          <div>
            <label className={label}>Venue / address</label>
            <input value={address} onChange={(e) => setAddress(e.target.value)} className={field} placeholder="123 Main St" />
          </div>
        ) : null}
        {locationType !== 'in_person' ? (
          <div>
            <label className={label}>Meeting link</label>
            <input value={meetingLink} onChange={(e) => setMeetingLink(e.target.value)} className={field} placeholder="https://…" />
          </div>
        ) : null}
      </section>

      <section className="space-y-4 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Guest list</h2>
        <div>
          <label className={label}>Invite a cohort</label>
          <select value={cohortId} onChange={(e) => setCohortId(e.target.value)} className={field}>
            <option value="">No cohort — invite guests individually</option>
            {cohorts.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.member_count})
              </option>
            ))}
          </select>
        </div>

        {!isEdit && cohortId ? (
          <div className="rounded border border-blue-100 bg-blue-50/60 p-3 dark:border-blue-900 dark:bg-blue-900/20">
            <label className="flex items-start gap-2 text-sm text-gray-800 dark:text-gray-200">
              <input
                type="checkbox"
                checked={inviteNow}
                onChange={(e) => setInviteNow(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                Send invitations to <strong>{selectedCohort?.name}</strong> now
                {selectedCohort ? ` (${selectedCohort.member_count} donors)` : ''}
              </span>
            </label>
            {inviteNow ? (
              <div className="mt-3 space-y-1.5 pl-6">
                {CHANNELS.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <input type="checkbox" checked={channels.includes(c.id)} onChange={() => toggleChannel(c.id)} />
                    {c.label}
                    <span className="text-xs text-gray-400">— {c.hint}</span>
                  </label>
                ))}
              </div>
            ) : (
              <p className="mt-1 pl-6 text-xs text-gray-500">
                Leave unchecked to attach the cohort now and invite later from the event.
              </p>
            )}
          </div>
        ) : null}
      </section>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button disabled={!canSave} onClick={submit}>
          {saving ? (
            <Loader2 size={15} className="mr-1 animate-spin" />
          ) : isEdit ? (
            <Save size={15} className="mr-1" />
          ) : inviteNow ? (
            <Send size={15} className="mr-1" />
          ) : (
            <CalendarPlus size={15} className="mr-1" />
          )}
          {saving ? 'Saving…' : isEdit ? 'Save changes' : inviteNow ? 'Create & send invitations' : 'Create event'}
        </Button>
      </div>
    </div>
  );
};

export default DonorEventForm;
